import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import type { DeviceSpecTag, EdgeConfigResult, EdgeDevice, EdgeMachineItem, EdgeMachineItemEdit, EdgeTag, MachineItemKind } from '../../types'
import { ITEM_SECTIONS, itemToEdit, mesLabel } from './shared'
import { aliasItemCode, tagAlias } from './specLookup'
import type { SpecTagOf } from './specLookup'
import type { TagReading, TagResolved } from './shared'
import { AlarmState, Empty, Section, TagRef } from './settingsUi'
import { ReadingTimeCell, ReadingValueCell } from './ui'

// 섹션 안에서는 이만큼만 보이고 나머지는 스크롤 (전체·편집은 팝업)
const VISIBLE_ROWS = 5

interface ItemView {
  kind: MachineItemKind
  items: EdgeMachineItem[]
  tagOf: (tagId: string) => TagResolved
  readingOf: (tag: TagResolved) => TagReading | null
}

// ═══════════════════════════════════════════════════════════════════════
// 보기 — 항목마다 태그 하나. 알람은 발생 값과 현재 값을 비교해 상태를 보여준다. thead 는 스크롤 영역 안에서 고정
// ═══════════════════════════════════════════════════════════════════════

function ItemTable({ kind, items, tagOf, readingOf }: ItemView) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
        <tr>
          <th className="text-left px-4 py-2">코드</th>
          <th className="text-left px-4 py-2">설명</th>
          <th className="text-left px-4 py-2">태그</th>
          {kind === 'alarm' && <th className="text-left px-4 py-2">발생 값</th>}
          <th className="text-left px-4 py-2">현재 값</th>
          {kind === 'alarm' ? <th className="text-left px-4 py-2">상태</th> : <th className="text-left px-4 py-2">수집 시각</th>}
          <th className="text-left px-4 py-2">MES</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {items.map(item => {
          const tag = tagOf(item.tagId)
          const reading = readingOf(tag)
          return (
            <tr key={item.tagId} className="hover:bg-gray-50">
              <td className="px-4 py-2.5 font-mono text-xs text-gray-800">{item.code}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{item.description || '-'}</td>
              <td className="px-4 py-2.5"><TagRef code={item.tagCode} tag={tag} /></td>
              {kind === 'alarm' && <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{item.alarmValue ?? '-'}</td>}
              <ReadingValueCell reading={reading} />
              {kind === 'alarm' ? (
                <td className="px-4 py-2.5"><AlarmState reading={reading} alarmValue={item.alarmValue} /></td>
              ) : (
                <ReadingTimeCell reading={reading} />
              )}
              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{mesLabel(item, kind)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// 스크롤 영역 높이를 thead + 앞 VISIBLE_ROWS 행의 실제 높이로 맞춘다 — 행 높이가 태그 설명 유무로 달라서 고정값을 쓰지 않는다
function useVisibleRowsHeight(rowCount: number) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const fit = () => {
      if (rowCount <= VISIBLE_ROWS) {
        el.style.maxHeight = ''
        return
      }
      const head = el.querySelector('thead')?.getBoundingClientRect().height ?? 0
      const rows = Array.from(el.querySelectorAll('tbody > tr')).slice(0, VISIBLE_ROWS)
      const body = rows.reduce((h, r) => h + r.getBoundingClientRect().height, 0)
      el.style.maxHeight = `${Math.ceil(head + body)}px`
    }
    fit()
    const table = el.querySelector('table')
    if (!table) return
    const observer = new ResizeObserver(fit)
    observer.observe(table)
    return () => observer.disconnect()
  }, [rowCount])

  return ref
}

export function ItemSection({ title, onOpen, lockReason, ...view }: ItemView & {
  title: string
  lockReason: string | null
  onOpen: (mode: 'view' | 'add') => void
}) {
  const scrollRef = useVisibleRowsHeight(view.items.length)
  const empty = view.items.length === 0

  return (
    <Section title={title} count={view.items.length}
      action={
        // 비어 있으면 바로 추가(편집)로 열린다 — 잠겨 있으면 막는다. 보기는 잠겨 있어도 된다
        <button onClick={() => onOpen(empty ? 'add' : 'view')} disabled={empty && !!lockReason}
          title={empty ? lockReason ?? undefined : undefined}
          className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed">
          {empty ? '+ 추가' : '전체 보기 · 편집'}
        </button>
      }>
      {empty ? (
        <Empty>설정된 {title} 항목이 없습니다.</Empty>
      ) : (
        <div ref={scrollRef} className="overflow-auto">
          <ItemTable {...view} />
        </div>
      )}
    </Section>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// 팝업 — 전체 보기 / 편집(추가·수정·삭제). 저장은 목록 통째 교체 (엣지 UpdateMachineItems)
// ═══════════════════════════════════════════════════════════════════════

interface ModalProps extends ItemView {
  title: string
  serverId: string
  machineCode: string
  devices: EdgeDevice[] | undefined
  specTagOf: SpecTagOf | null // 이 서버에서 고른 명세서의 같은 태그 (명세서가 없으면 null)
  lists: ItemLists // 이 설비의 네 목록 — 다른 목록에서 옮겨 올 때 쓴다
  initialMode: 'view' | 'add'
  lockReason: string | null
  onSaved: (result: EdgeConfigResult) => void
  onClose: () => void
}

export type ItemLists = Record<MachineItemKind, EdgeMachineItem[]>

export function ItemListModal({ title, serverId, machineCode, devices, specTagOf, lists, initialMode, lockReason, onSaved, onClose, ...view }: ModalProps) {
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode === 'add' ? 'edit' : 'view')
  const [resending, setResending] = useState(false)
  const dirtyRef = useRef(false)

  // 다시 보내기 — 지금 보이는 목록 그대로 엣지에 한 번 더 저장 (전송이 안 됐을 때 대비)
  const resend = async () => {
    if (!confirm(`${title} ${view.items.length}개 항목을 엣지에 다시 보낼까요?`)) return
    setResending(true)
    try {
      const { data } = await serversApi.updateMachineItems(serverId, machineCode, view.kind, view.items.map(i => itemToEdit(i, view.kind)))
      onSaved(data)
    } catch (err) {
      onSaved({ success: false, message: getErrorMessage(err, '다시 보내지 못했습니다.') })
    } finally {
      setResending(false)
    }
  }

  const requestClose = () => {
    if (mode === 'edit' && dirtyRef.current && !confirm('저장하지 않은 변경 사항이 있습니다. 닫을까요?')) return
    onClose()
  }
  const closeRef = useRef(requestClose)
  useEffect(() => { closeRef.current = requestClose })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onMouseDown={requestClose}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col overflow-hidden"
        onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <span className="text-xs text-gray-400">{view.items.length}</span>
          {mode === 'edit' && <span className="text-xs text-blue-600 font-medium ml-1">편집 중</span>}
          <div className="ml-auto flex items-center gap-3">
            {mode === 'view' && (
              <>
                {lockReason && <span className="text-xs text-gray-400">{lockReason}</span>}
                <button onClick={resend} disabled={!!lockReason || resending || view.items.length === 0}
                  title="지금 목록을 엣지(LiveEdge)에 다시 저장합니다"
                  className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
                  {resending ? '보내는 중…' : '다시 보내기'}
                </button>
                <button onClick={() => setMode('edit')} disabled={!!lockReason}
                  className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
                  편집
                </button>
              </>
            )}
            <button onClick={requestClose} className="text-gray-400 hover:text-gray-600" aria-label="닫기">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {mode === 'view' ? (
          <div className="min-h-0 overflow-auto">
            <ItemTable {...view} />
          </div>
        ) : (
          <ItemEditor
            {...view}
            serverId={serverId}
            machineCode={machineCode}
            devices={devices}
            specTagOf={specTagOf}
            title={title}
            lists={lists}
            startWithPicker={initialMode === 'add'}
            onDirtyChange={dirty => { dirtyRef.current = dirty }}
            onCancel={() => {
              if (dirtyRef.current && !confirm('변경 사항을 버릴까요?')) return
              dirtyRef.current = false
              if (initialMode === 'add' && view.items.length === 0) onClose()
              else setMode('view')
            }}
            onSaved={result => {
              dirtyRef.current = false
              setMode('view')
              onSaved(result)
            }}
          />
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// 편집기
// ═══════════════════════════════════════════════════════════════════════

interface Draft {
  tagId: string
  code: string
  description: string
  mesRegisterable: boolean
  alarmValue: string
  changedOnly: boolean
  mesInterval: string
  from?: MachineItemKind // 다른 목록에서 옮겨 온 항목 — 저장할 때 그 목록에서 뺀다
}

type DraftErrors = Partial<Record<'code' | 'alarmValue' | 'mesInterval' | 'tag', string>>

const toDraft = (item: EdgeMachineItem): Draft => ({
  tagId: item.tagId,
  code: item.code,
  description: item.description ?? '',
  mesRegisterable: item.mesRegisterable,
  alarmValue: item.alarmValue ?? '',
  changedOnly: item.changedOnly ?? true,
  mesInterval: item.mesInterval != null ? String(item.mesInterval) : '5000',
})

// 코드로 쓸 태그 — 태그 코드 그대로 ([디바이스]주소[크기])
const tagValueOf = (tag: TagResolved) => tag?.code ?? ''

// 새 항목 기본값 — 코드는 태그 Alias 로 {설비코드}_{Alias} (Edge 태그 Alias, 비면 명세 Alias), 둘 다 비면 태그 코드.
// 설명은 명세 태그 설명. Edge 태그 Description 은 메모라 쓰지 않는다. MES 는 꺼 둔다
const draftFromTag = (tag: EdgeTag, machineCode: string, specTagOf: SpecTagOf | null): Draft => ({
  tagId: tag.id,
  code: aliasItemCode(machineCode, tag, specTagOf?.(tag.id) ?? null) ?? tagValueOf(tag),
  description: specTagOf?.(tag.id)?.description.trim() ?? '',
  mesRegisterable: false,
  alarmValue: '',
  changedOnly: true,
  mesInterval: '5000',
})

// 서버(엣지 Em2ConfigHandlers.UpdateMachineItems)와 같은 규칙 — 저장 전에 화면에서 먼저 알려준다
function validate(drafts: Draft[], kind: MachineItemKind, tagOf: (tagId: string) => TagResolved) {
  const errors: Record<string, DraftErrors> = {}
  const codeCount = new Map<string, number>()
  drafts.forEach(d => codeCount.set(d.code.trim(), (codeCount.get(d.code.trim()) ?? 0) + 1))

  for (const d of drafts) {
    const e: DraftErrors = {}
    if (!d.code.trim()) e.code = '필수'
    else if ((codeCount.get(d.code.trim()) ?? 0) > 1) e.code = '중복'
    if (kind === 'alarm' && !d.alarmValue.trim()) e.alarmValue = '필수'
    if (kind === 'signal' && d.mesRegisterable && !d.changedOnly) {
      const ms = Number(d.mesInterval)
      if (!Number.isInteger(ms) || ms < 200) e.mesInterval = '200 이상'
    }
    if (tagOf(d.tagId) === null) e.tag = '디바이스에 없는 태그입니다. 삭제하세요'
    if (Object.keys(e).length > 0) errors[d.tagId] = e
  }
  return errors
}

const toEdit = (d: Draft, kind: MachineItemKind): EdgeMachineItemEdit => ({
  tagId: d.tagId,
  code: d.code.trim(),
  description: d.description.trim() || null,
  mesRegisterable: d.mesRegisterable,
  alarmValue: kind === 'alarm' ? d.alarmValue.trim() : null,
  changedOnly: d.changedOnly,
  mesInterval: kind === 'signal' && d.mesRegisterable && !d.changedOnly ? Number(d.mesInterval) : null,
})
const titleOf = (kind: MachineItemKind) => ITEM_SECTIONS.find(s => s.kind === kind)?.title ?? kind

function ItemEditor({ kind, items, tagOf, readingOf, serverId, machineCode, devices, specTagOf, title, lists, startWithPicker, onDirtyChange, onCancel, onSaved }: ItemView & {
  serverId: string
  machineCode: string
  devices: EdgeDevice[] | undefined
  specTagOf: SpecTagOf | null
  title: string
  lists: ItemLists
  startWithPicker: boolean
  onDirtyChange: (dirty: boolean) => void
  onCancel: () => void
  onSaved: (result: EdgeConfigResult) => void
}) {
  const [drafts, setDrafts] = useState<Draft[]>(() => items.map(toDraft))
  const [pickerOpen, setPickerOpen] = useState(startWithPicker)
  const [moveOpen, setMoveOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [checked, setChecked] = useState<string[]>([]) // 일괄 작업 대상 행 (tagId)
  const [focusTarget, setFocusTarget] = useState<string | null>(null) // 이동할 행 (tagId) — 렌더 뒤 스크롤·포커스
  const listRef = useRef<HTMLDivElement>(null)

  const original = useMemo(() => JSON.stringify(items.map(toDraft)), [items])
  const dirty = JSON.stringify(drafts) !== original
  useEffect(() => { onDirtyChange(dirty) }, [dirty, onDirtyChange])

  const errors = validate(drafts, kind, tagOf)
  const errorCount = Object.keys(errors).length
  const usedTagIds = useMemo(() => new Set(drafts.map(d => d.tagId)), [drafts])

  const update = (tagId: string, patch: Partial<Draft>) =>
    setDrafts(ds => ds.map(d => (d.tagId === tagId ? { ...d, ...patch } : d)))

  // 확인이 필요한 행으로 이동 — 그 행의 첫 오류 입력칸(없으면 코드칸)에 포커스
  useEffect(() => {
    if (!focusTarget) return
    const row = listRef.current?.querySelector<HTMLElement>(`[data-row="${CSS.escape(focusTarget)}"]`)
    const field = row?.querySelector<HTMLInputElement>('[aria-invalid="true"]') ?? row?.querySelector<HTMLInputElement>('[data-field="code"]')
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    field?.focus({ preventScroll: true })
    setFocusTarget(null)
  }, [focusTarget])

  const errorIds = drafts.filter(d => errors[d.tagId]).map(d => d.tagId)
  const [errorCursor, setErrorCursor] = useState(0)
  const goToError = (index: number) => {
    if (errorIds.length === 0) return
    const i = ((index % errorIds.length) + errorIds.length) % errorIds.length
    setErrorCursor(i)
    setShowErrors(true)
    setFocusTarget(errorIds[i])
  }

  // 일괄 작업 대상 — 선택한 행이 있으면 그 행들, 없으면 전체
  const targetIds = checked.length > 0 ? new Set(checked) : null
  const targetLabel = checked.length > 0 ? `선택 ${checked.length}` : '전체'
  const allChecked = drafts.length > 0 && checked.length === drafts.length

  // Alias — {설비코드}_{Alias} (Edge 태그 Alias, 비면 명세 Alias). 둘 다 빈 행은 그대로 둔다
  const setCodesFrom = (source: 'alias' | 'tag') =>
    setDrafts(ds => ds.map(d => (targetIds && !targetIds.has(d.tagId)) ? d : {
      ...d,
      code: source === 'alias'
        ? aliasItemCode(machineCode, tagOf(d.tagId), specTagOf?.(d.tagId) ?? null) ?? d.code
        : tagValueOf(tagOf(d.tagId)),
    }))

  // MES 열 머리 체크 — 전체 켜기/끄기
  const allMes = drafts.length > 0 && drafts.every(d => d.mesRegisterable)
  const someMes = drafts.some(d => d.mesRegisterable)
  const toggleAllMes = () => setDrafts(ds => ds.map(d => ({ ...d, mesRegisterable: !allMes })))

  // 신호 MES 전송 일괄 설정 — 대상(선택 또는 전체) 중 MES 를 켠 행에만
  const [bulkSend, setBulkSend] = useState<'changed' | 'interval'>('changed')
  const [bulkInterval, setBulkInterval] = useState('5000')
  const bulkSendCount = drafts.filter(d => d.mesRegisterable && (!targetIds || targetIds.has(d.tagId))).length
  const applyBulkSend = () => setDrafts(ds => ds.map(d => (!d.mesRegisterable || (targetIds && !targetIds.has(d.tagId))) ? d : {
    ...d,
    changedOnly: bulkSend === 'changed',
    mesInterval: bulkSend === 'interval' ? bulkInterval : d.mesInterval,
  }))

  const removeChecked = () => {
    const ids = new Set(checked)
    setDrafts(ds => ds.filter(d => !ids.has(d.tagId)))
    setChecked([])
  }

  const toggleChecked = (tagId: string) =>
    setChecked(c => (c.includes(tagId) ? c.filter(x => x !== tagId) : [...c, tagId]))

  const save = async () => {
    setShowErrors(true)
    if (errorCount > 0) {
      goToError(0)
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      const { data } = await serversApi.updateMachineItems(serverId, machineCode, kind, drafts.map(d => toEdit(d, kind)))
      // 옮겨 온 항목은 이 목록 저장이 끝난 뒤에 원래 목록에서 뺀다 — 중간에 실패해도 항목을 잃지 않고 두 목록에 남는다
      const failed: string[] = []
      for (const { kind: source, title: sourceTitle } of ITEM_SECTIONS) {
        const moved = new Set(drafts.filter(d => d.from === source).map(d => d.tagId))
        if (moved.size === 0) continue
        const rest = lists[source].filter(i => !moved.has(i.tagId)).map(i => itemToEdit(i, source))
        try {
          await serversApi.updateMachineItems(serverId, machineCode, source, rest)
        } catch (e) {
          failed.push(`${sourceTitle}: ${getErrorMessage(e, '저장하지 못했습니다.')}`)
        }
      }
      onSaved(failed.length === 0 ? data : {
        success: false,
        message: `${title}에는 저장했지만 원래 목록에서 빼지 못해 두 곳에 남아 있습니다 — ${failed.join(' / ')}`,
      })
    } catch (err) {
      setSaveError(getErrorMessage(err, '저장하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const err = (tagId: string, field: keyof DraftErrors) => (showErrors ? errors[tagId]?.[field] : undefined)
  const toolButton = 'border border-gray-300 text-gray-600 px-2.5 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed'
  const input = (invalid: boolean) =>
    `w-full border rounded-md px-2 py-1 text-xs ${invalid ? 'border-red-400 bg-red-50' : 'border-gray-300'}`

  return (
    <>
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 shrink-0">
        <button onClick={() => setPickerOpen(true)} disabled={!devices}
          title={devices ? undefined : '디바이스 목록을 받지 못했습니다'}
          className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-accent-hover transition disabled:opacity-50">
          + 태그에서 추가
        </button>
        <button onClick={() => setMoveOpen(true)} className={toolButton}
          disabled={ITEM_SECTIONS.every(s => s.kind === kind || lists[s.kind].length === 0)}
          title={`다른 목록(${ITEM_SECTIONS.filter(s => s.kind !== kind).map(s => s.title).join('·')})의 항목을 골라 ${title}로 옮깁니다`}>
          다른 목록에서 옮겨 오기
        </button>
        <span className="text-xs text-gray-400">
          {drafts.length}개{dirty ? ' · 변경됨' : ''}
        </span>

        {/* 일괄 작업 — 선택한 행이 없으면 전체에 적용 */}
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <button onClick={() => setCodesFrom('alias')} disabled={drafts.length === 0} className={toolButton}
            title={`코드를 태그 Alias 기준으로 바꿉니다 (예: ${machineCode}_CNT) — Edge 태그 Alias 가 비면 ${specTagOf ? '명세 Alias' : '그대로 둡니다'}`}>
            코드 ← Alias <span className="text-gray-400">({targetLabel})</span>
          </button>
          <button onClick={() => setCodesFrom('tag')} disabled={drafts.length === 0} className={toolButton}
            title="코드를 태그 코드로 바꿉니다">
            코드 ← 태그 <span className="text-gray-400">({targetLabel})</span>
          </button>
          <button onClick={removeChecked} disabled={checked.length === 0}
            className={`${toolButton} text-red-600 border-red-200 hover:bg-red-50`}>
            선택 삭제{checked.length > 0 ? ` (${checked.length})` : ''}
          </button>
        </div>
      </div>

      {showErrors && errorCount > 0 && (
        <div className="flex items-center gap-3 px-5 py-2 bg-red-50 border-b border-red-100 text-xs text-red-700 shrink-0">
          <span>확인이 필요한 항목 {errorCount}개 (코드가 비어 있거나 중복됨)</span>
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => goToError(errorCursor - 1)} className="px-2 py-0.5 rounded hover:bg-red-100">↑ 이전</button>
            <span className="text-red-400">{Math.min(errorCursor + 1, errorCount)}/{errorCount}</span>
            <button onClick={() => goToError(errorCursor + 1)} className="px-2 py-0.5 rounded hover:bg-red-100">다음 ↓</button>
          </div>
        </div>
      )}

      <div ref={listRef} className="min-h-0 flex-1 overflow-auto">
        {drafts.length === 0 ? (
          <Empty>항목이 없습니다. "태그에서 추가"로 디바이스 태그를 골라 넣으세요.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
              <tr>
                <th className="w-9 pl-4 pr-1 py-2">
                  <input type="checkbox" checked={allChecked} aria-label="전체 선택"
                    onChange={() => setChecked(allChecked ? [] : drafts.map(d => d.tagId))}
                    className="w-4 h-4 accent-blue-600 align-middle" />
                </th>
                <th className="text-left px-3 py-2">태그</th>
                <th className="text-left px-3 py-2">현재 값</th>
                <th className="text-left px-3 py-2 w-40">코드 <span className="text-red-500">*</span></th>
                <th className="text-left px-3 py-2">설명</th>
                {kind === 'alarm' && <th className="text-left px-3 py-2 w-28">발생 값 <span className="text-red-500">*</span></th>}
                <th className="text-left px-3 py-2 w-16">
                  <label className="flex items-center gap-1.5 cursor-pointer" title="전체 MES 켜기/끄기">
                    <input type="checkbox" checked={allMes} disabled={drafts.length === 0} aria-label="MES 전체 선택"
                      ref={el => { if (el) el.indeterminate = someMes && !allMes }}
                      onChange={toggleAllMes} className="w-4 h-4 accent-blue-600" />
                    MES
                  </label>
                </th>
                {kind === 'signal' && (
                  <th className="text-left px-3 py-2 w-64">
                    <div>MES 전송</div>
                    {/* 일괄 설정 — 선택한 행이 있으면 그 행들, 없으면 전체 중 MES 를 켠 행에 */}
                    <div className="flex items-center gap-1 mt-1 font-normal">
                      <select value={bulkSend} onChange={e => setBulkSend(e.target.value as 'changed' | 'interval')}
                        className="border border-gray-300 rounded-md px-1.5 py-0.5 text-xs bg-white">
                        <option value="changed">변경 시</option>
                        <option value="interval">주기</option>
                      </select>
                      {bulkSend === 'interval' && (
                        <input type="number" min={200} step={100} value={bulkInterval} onChange={e => setBulkInterval(e.target.value)}
                          aria-label="일괄 주기(ms)" className="border border-gray-300 rounded-md px-1.5 py-0.5 text-xs w-20 bg-white" />
                      )}
                      <button onClick={applyBulkSend} disabled={bulkSendCount === 0}
                        title={bulkSendCount === 0 ? 'MES 를 켠 행이 없습니다' : `MES 를 켠 ${bulkSendCount}개 행에 적용`}
                        className="border border-gray-300 text-gray-600 px-2 py-0.5 rounded-md text-xs bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                        적용 <span className="text-gray-400">({targetLabel})</span>
                      </button>
                    </div>
                  </th>
                )}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {drafts.map(d => {
                const tag = tagOf(d.tagId)
                const isNew = !items.some(i => i.tagId === d.tagId)
                return (
                  <tr key={d.tagId} data-row={d.tagId}
                    className={checked.includes(d.tagId) ? 'bg-blue-50' : isNew ? 'bg-blue-50/40' : ''}>
                    <td className="pl-4 pr-1 py-2 align-top">
                      <input type="checkbox" checked={checked.includes(d.tagId)} onChange={() => toggleChecked(d.tagId)}
                        aria-label="선택" className="mt-1.5 w-4 h-4 accent-blue-600" />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <TagRef code={null} tag={tag} />
                      {d.from && <div className="text-[11px] text-blue-600 mt-0.5">{titleOf(d.from)}에서 옮겨 옴</div>}
                      {err(d.tagId, 'tag') && <div className="text-xs text-red-600 mt-0.5">{err(d.tagId, 'tag')}</div>}
                    </td>
                    <ReadingValueCell reading={readingOf(tag)} className="align-top text-xs" />
                    <td className="px-3 py-2 align-top">
                      <input value={d.code} onChange={e => update(d.tagId, { code: e.target.value })}
                        data-field="code" aria-invalid={!!err(d.tagId, 'code')}
                        className={`${input(!!err(d.tagId, 'code'))} font-mono`} />
                      {err(d.tagId, 'code') && <div className="text-[11px] text-red-600 mt-0.5">{err(d.tagId, 'code')}</div>}
                      <AliasHint tag={tag} specTag={specTagOf?.(d.tagId) ?? null} hasSpec={!!specTagOf} />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <input value={d.description} onChange={e => update(d.tagId, { description: e.target.value })}
                        className={input(false)} />
                    </td>
                    {kind === 'alarm' && (
                      <td className="px-3 py-2 align-top">
                        <input value={d.alarmValue} onChange={e => update(d.tagId, { alarmValue: e.target.value })}
                          aria-invalid={!!err(d.tagId, 'alarmValue')}
                          className={`${input(!!err(d.tagId, 'alarmValue'))} font-mono`} />
                        {err(d.tagId, 'alarmValue') && <div className="text-[11px] text-red-600 mt-0.5">{err(d.tagId, 'alarmValue')}</div>}
                      </td>
                    )}
                    <td className="px-3 py-2 align-top">
                      <input type="checkbox" checked={d.mesRegisterable}
                        onChange={e => update(d.tagId, { mesRegisterable: e.target.checked })}
                        className="mt-1.5 w-4 h-4 accent-blue-600" />
                    </td>
                    {kind === 'signal' && (
                      <td className="px-3 py-2 align-top">
                        {/* MES 등록일 때만 의미 — 변경 시 전송이면 주기는 쓰지 않는다 */}
                        {d.mesRegisterable ? (
                          <div className="flex items-start gap-2">
                            <select value={d.changedOnly ? 'changed' : 'interval'}
                              onChange={e => update(d.tagId, { changedOnly: e.target.value === 'changed' })}
                              className="border border-gray-300 rounded-md px-2 py-1 text-xs">
                              <option value="changed">변경 시</option>
                              <option value="interval">주기</option>
                            </select>
                            {!d.changedOnly && (
                              <div className="min-w-0">
                                <div className="flex items-center gap-1">
                                  <input type="number" min={200} step={100} value={d.mesInterval}
                                    onChange={e => update(d.tagId, { mesInterval: e.target.value })}
                                    aria-invalid={!!err(d.tagId, 'mesInterval')}
                                    className={`${input(!!err(d.tagId, 'mesInterval'))} w-24`} />
                                  <span className="text-xs text-gray-400">ms</span>
                                </div>
                                {err(d.tagId, 'mesInterval') && <div className="text-[11px] text-red-600 mt-0.5">{err(d.tagId, 'mesInterval')}</div>}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300 leading-7">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-2 py-2 align-top">
                      <button onClick={() => {
                        setDrafts(ds => ds.filter(x => x.tagId !== d.tagId))
                        setChecked(c => c.filter(x => x !== d.tagId))
                      }}
                        className="p-1 text-gray-400 hover:text-red-600 transition" aria-label="삭제" title="삭제">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0-.8 12a2 2 0 0 1-2 1.9H8.8a2 2 0 0 1-2-1.9L6 7" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
        {saveError ? (
          <span className="text-xs text-red-600">{saveError}</span>
        ) : (
          <span className="text-xs text-gray-400">
            저장하면 엣지 설정에 바로 반영됩니다{drafts.some(d => d.from) ? ' (옮겨 온 항목은 원래 목록에서 빠집니다)' : ''}. 모니터링을 시작하면 새 설정으로 수집합니다.
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={onCancel} disabled={saving}
            className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">
            취소
          </button>
          <button onClick={save} disabled={saving || !dirty}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>

      {pickerOpen && devices && (
        <TagPicker
          devices={devices}
          usedTagIds={usedTagIds}
          specTagOf={specTagOf}
          onClose={() => setPickerOpen(false)}
          onAdd={tags => {
            const added = tags.map(t => draftFromTag(t, machineCode, specTagOf))
            setDrafts(ds => [...ds, ...added])
            setPickerOpen(false)
            // 코드가 빈 항목이 있으면 바로 짚어 준다 (아니면 추가한 첫 행으로)
            const empty = added.find(d => !d.code.trim())
            if (empty) setShowErrors(true)
            setFocusTarget((empty ?? added[0])?.tagId ?? null)
          }}
        />
      )}

      {moveOpen && (
        <MovePicker
          kind={kind}
          title={title}
          lists={lists}
          usedTagIds={usedTagIds}
          tagOf={tagOf}
          onClose={() => setMoveOpen(false)}
          onMove={picked => {
            // 태그·코드·설명만 가져온다 — MES·발생 값 같은 종류별 옵션은 새 항목 기본값으로 (알람이면 발생 값을 새로 넣어야 한다)
            const added = picked.map(({ item, from }): Draft => ({
              tagId: item.tagId,
              code: item.code,
              description: item.description ?? '',
              mesRegisterable: false,
              alarmValue: '',
              changedOnly: true,
              mesInterval: '5000',
              from,
            }))
            setDrafts(ds => [...ds, ...added])
            setMoveOpen(false)
            const invalid = kind === 'alarm' ? added.find(d => !d.alarmValue.trim()) : undefined
            if (invalid) setShowErrors(true)
            setFocusTarget((invalid ?? added[0])?.tagId ?? null)
          }}
        />
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// 다른 목록에서 옮겨 오기 — 왼쪽 목록(Alarm·Mete·Parameter·Signal 중 지금 것 빼고), 오른쪽 항목.
// 고른 항목만 편집 중인 목록에 넣고, 원래 목록에서 빼는 건 저장할 때 한다
// ═══════════════════════════════════════════════════════════════════════

function MovePicker({ kind, title, lists, usedTagIds, tagOf, onClose, onMove }: {
  kind: MachineItemKind
  title: string
  lists: ItemLists
  usedTagIds: Set<string>
  tagOf: (tagId: string) => TagResolved
  onClose: () => void
  onMove: (picked: { item: EdgeMachineItem; from: MachineItemKind }[]) => void
}) {
  const sources = ITEM_SECTIONS.filter(s => s.kind !== kind)
  // 처음엔 Mete — 비어 있으면 항목이 있는 첫 목록
  const [source, setSource] = useState<MachineItemKind>(
    () => (lists.mete.length > 0 && kind !== 'mete' ? 'mete' : sources.find(s => lists[s.kind].length > 0)?.kind ?? sources[0].kind))
  // tagId → 어느 목록에서 골랐는지 (같은 태그가 두 목록에 있으면 한 곳에서만 고를 수 있다)
  const [selected, setSelected] = useState<Map<string, MachineItemKind>>(new Map())
  const [query, setQuery] = useState('')

  const q = query.trim().toLowerCase()
  const visible = lists[source].filter(i =>
    !q || [i.code, i.description, i.tagCode].some(v => v?.toLowerCase().includes(q)))
  const selectable = visible.filter(i => !usedTagIds.has(i.tagId) && (selected.get(i.tagId) ?? source) === source)
  const allVisibleSelected = selectable.length > 0 && selectable.every(i => selected.has(i.tagId))

  const toggle = (tagId: string) => setSelected(s => {
    const next = new Map(s)
    if (next.has(tagId)) next.delete(tagId)
    else next.set(tagId, source)
    return next
  })
  const toggleAllVisible = () => setSelected(s => {
    const next = new Map(s)
    selectable.forEach(i => (allVisibleSelected ? next.delete(i.tagId) : next.set(i.tagId, source)))
    return next
  })
  const selectedCountOf = (k: MachineItemKind) => [...selected.values()].filter(v => v === k).length

  const pick = () => onMove(sources.flatMap(s =>
    lists[s.kind].filter(i => selected.get(i.tagId) === s.kind).map(item => ({ item, from: s.kind }))))

  // Esc 는 선택기만 닫는다 (TagPicker 와 같음)
  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      closeRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60] p-4" onMouseDown={onClose}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden"
        onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">{title}로 옮겨 오기</h3>
          <span className="text-xs text-gray-400">고른 항목의 태그·코드·설명만 옮깁니다 (MES 등 옵션은 새로 설정). 저장하면 원래 목록에서 빠집니다</span>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600" aria-label="닫기">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 flex">
          <div className="w-48 shrink-0 border-r border-gray-100 overflow-y-auto py-2">
            {sources.map(s => {
              const count = selectedCountOf(s.kind)
              return (
                <button key={s.kind} onClick={() => setSource(s.kind)}
                  className={`w-full text-left px-4 py-2 border-l-2 transition ${
                    s.kind === source ? 'border-blue-600 bg-blue-50' : 'border-transparent hover:bg-gray-50'
                  }`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${s.kind === source ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{s.title}</span>
                    <span className="text-[11px] text-gray-400">{lists[s.kind].length}</span>
                    {count > 0 && (
                      <span className="ml-auto shrink-0 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-medium">{count}</span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 shrink-0">
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
                  disabled={selectable.length === 0} className="w-4 h-4 accent-blue-600" />
                {q ? '검색 결과 전체' : '이 목록 전체'}
              </label>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="코드·설명·태그 검색"
                className="ml-auto border border-gray-300 rounded-lg px-3 py-1.5 text-xs w-56" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
                  <tr>
                    <th className="w-10"></th>
                    <th className="text-left px-3 py-2">코드</th>
                    <th className="text-left px-3 py-2">설명</th>
                    <th className="text-left px-3 py-2">태그</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visible.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">항목이 없습니다.</td></tr>
                  ) : visible.map(i => {
                    const used = usedTagIds.has(i.tagId)
                    const elsewhere = !used && selected.has(i.tagId) && selected.get(i.tagId) !== source
                    const disabled = used || elsewhere
                    const checked = selected.get(i.tagId) === source
                    return (
                      <tr key={i.tagId} onClick={() => !disabled && toggle(i.tagId)}
                        className={disabled ? 'opacity-50' : `cursor-pointer ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={used || checked} disabled={disabled} readOnly
                            className="w-4 h-4 accent-blue-600 pointer-events-none" />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-800">{i.code}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">{i.description || '-'}</td>
                        <td className="px-3 py-2">
                          <TagRef code={i.tagCode} tag={tagOf(i.tagId)} />
                          {used && <span className="ml-2 text-[11px] text-gray-400">{title}에 이미 있음</span>}
                          {elsewhere && <span className="ml-2 text-[11px] text-gray-400">{titleOf(selected.get(i.tagId)!)}에서 선택됨</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 border-t border-gray-100 px-5 py-3 shrink-0">
          <span className="text-xs text-gray-500">
            선택한 항목 {selected.size}개
            {kind === 'alarm' && selected.size > 0 && <span className="text-gray-400"> · 알람은 발생 값을 넣어야 저장할 수 있습니다</span>}
          </span>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">
              취소
            </button>
            <button onClick={pick} disabled={selected.size === 0}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
              {selected.size > 0 ? `${selected.size}개 옮겨 오기` : '옮겨 오기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// 태그 선택기 — 왼쪽 디바이스, 오른쪽 태그. 선택은 디바이스와 무관하게 한 집합으로 들고 있어서
// 다른 디바이스로 옮겨 가도 앞에서 고른 태그가 유지된다 (아래 "선택한 태그"에 모아 보여준다)
// ═══════════════════════════════════════════════════════════════════════

// 코드 칸 아래 — 이 태그의 Alias. Edge 태그 Alias 가 비었으면 명세 Alias (명세서를 지정했을 때만)
function AliasHint({ tag, specTag, hasSpec }: { tag: TagResolved; specTag: DeviceSpecTag | null; hasSpec: boolean }) {
  const edgeAlias = tag?.alias?.trim()
  if (edgeAlias) {
    return <div className="text-[11px] text-gray-400 mt-0.5">Alias <span className="font-mono text-gray-600">{edgeAlias}</span></div>
  }
  if (!hasSpec) return <div className="text-[11px] text-gray-300 mt-0.5">Alias (비어 있음)</div>
  if (!specTag) return <div className="text-[11px] text-gray-300 mt-0.5">Alias 비어 있음 · 명세에 없는 태그</div>
  return (
    <div className="text-[11px] text-gray-400 mt-0.5" title={specTag.description || undefined}>
      명세 Alias <span className="font-mono text-gray-600">{specTag.alias.trim() || '(비어 있음)'}</span>
    </div>
  )
}

function TagPicker({ devices, usedTagIds, specTagOf, onClose, onAdd }: {
  devices: EdgeDevice[]
  usedTagIds: Set<string>
  specTagOf: SpecTagOf | null
  onClose: () => void
  onAdd: (tags: EdgeTag[]) => void
}) {
  const [deviceCode, setDeviceCode] = useState(devices[0]?.code ?? '')
  const [selected, setSelected] = useState<string[]>([]) // 고른 순서 유지
  const [query, setQuery] = useState('')

  const device = devices.find(d => d.code === deviceCode)
  const tagById = useMemo(() => new Map(devices.flatMap(d => d.tags).map(t => [t.id, t])), [devices])
  const deviceOfTag = useMemo(
    () => new Map(devices.flatMap(d => d.tags.map(t => [t.id, d] as const))),
    [devices])

  const q = query.trim().toLowerCase()
  const visibleTags = (device?.tags ?? []).filter(t =>
    !q || [t.code, t.address, tagAlias(t, specTagOf?.(t.id) ?? null)].some(v => v?.toLowerCase().includes(q)))
  const selectable = visibleTags.filter(t => !usedTagIds.has(t.id))
  const allVisibleSelected = selectable.length > 0 && selectable.every(t => selected.includes(t.id))

  const toggle = (id: string) =>
    setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]))
  const toggleAllVisible = () =>
    setSelected(s => allVisibleSelected
      ? s.filter(id => !selectable.some(t => t.id === id))
      : [...s, ...selectable.map(t => t.id).filter(id => !s.includes(id))])

  const selectedCountOf = (d: EdgeDevice) => d.tags.filter(t => selected.includes(t.id)).length

  // Esc 는 선택기만 닫는다 — 캡처 단계에서 먼저 받아 아래 편집 팝업의 Esc 처리로 넘기지 않는다
  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      closeRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60] p-4" onMouseDown={onClose}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden"
        onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">태그 선택</h3>
          <span className="text-xs text-gray-400">여러 디바이스에서 골라 한 번에 추가할 수 있습니다</span>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600" aria-label="닫기">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 flex">
          <div className="w-60 shrink-0 border-r border-gray-100 overflow-y-auto py-2">
            {devices.map(d => {
              const count = selectedCountOf(d)
              return (
                <button key={d.code} onClick={() => setDeviceCode(d.code)}
                  className={`w-full text-left px-4 py-2 border-l-2 transition ${
                    d.code === deviceCode ? 'border-blue-600 bg-blue-50' : 'border-transparent hover:bg-gray-50'
                  }`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm truncate ${d.code === deviceCode ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{d.name}</span>
                    {count > 0 && (
                      <span className="ml-auto shrink-0 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-medium">{count}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400 font-mono truncate">{d.code} · 태그 {d.tags.length}</div>
                </button>
              )
            })}
          </div>

          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 shrink-0">
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
                  disabled={selectable.length === 0} className="w-4 h-4 accent-blue-600" />
                {q ? '검색 결과 전체' : '이 디바이스 전체'}
              </label>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="주소·코드·Alias 검색"
                className="ml-auto border border-gray-300 rounded-lg px-3 py-1.5 text-xs w-56" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
                  <tr>
                    <th className="w-10"></th>
                    <th className="text-left px-3 py-2">주소</th>
                    <th className="text-left px-3 py-2">코드</th>
                    <th className="text-left px-3 py-2">타입</th>
                    <th className="text-left px-3 py-2">Alias</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visibleTags.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">태그가 없습니다.</td></tr>
                  ) : visibleTags.map(t => {
                    const used = usedTagIds.has(t.id)
                    const checked = selected.includes(t.id)
                    return (
                      <tr key={t.id} onClick={() => !used && toggle(t.id)}
                        className={used ? 'opacity-50' : `cursor-pointer ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={used || checked} disabled={used} readOnly
                            className="w-4 h-4 accent-blue-600 pointer-events-none" />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">{t.address || '-'}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-500">{t.code}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">{t.dataType}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          <span className="font-mono">{tagAlias(t, specTagOf?.(t.id) ?? null) || '-'}</span>
                          {used && <span className="ml-2 text-[11px] text-gray-400">이미 등록됨</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 px-5 py-3 shrink-0">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs text-gray-500 mb-1.5">선택한 태그 {selected.length}개</div>
              <div className="flex flex-wrap gap-1.5 max-h-16 overflow-y-auto">
                {selected.length === 0 ? (
                  <span className="text-xs text-gray-300">디바이스를 옮겨 가며 골라도 선택이 유지됩니다</span>
                ) : selected.map(id => {
                  const t = tagById.get(id)
                  return (
                    <span key={id} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">
                      <span className="text-gray-400">{deviceOfTag.get(id)?.name}</span>
                      <span className="font-mono">{t?.address || t?.code}</span>
                      <button onClick={() => toggle(id)} className="text-gray-400 hover:text-gray-700 px-0.5" aria-label="선택 해제">×</button>
                    </span>
                  )
                })}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={onClose}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">
                취소
              </button>
              <button onClick={() => onAdd(selected.map(id => tagById.get(id)).filter((t): t is EdgeTag => !!t))}
                disabled={selected.length === 0}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
                {selected.length > 0 ? `${selected.length}개 추가` : '추가'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
